import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    viewChildren,
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {LoadComponent, PaginationComponent} from '@brunata-angular/ui';
import {
    caseInsensitiveComparator,
    composeComparators,
    filterNulls,
    fromControl,
    includesIgnoreCase,
    keyComparator,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {InputDropdownCheckboxesComponent, SelectableItem} from '@brunata-angular/ui';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {applySortingToHeaders, SortableTableHeaderComponent, Sorting} from '@brunata-angular/ui';
import {VariableAlarmWithTranslations} from '../variable-alarm.model';
import {
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import {uniqBy} from 'lodash-es';
import {VariableAlarmsService} from '../variable-alarms.service';
import {BehaviorSubject, combineLatest, map, take, tap} from 'rxjs';

type ColumnHeader = {
    label: string;
    column: string;
    formControl: FormControl<string | SelectableItem[] | boolean | null>;
    formType: 'text' | 'selectMultiple';
};
type Filter = {
    meterCriteriaMismatch: SelectableItem[];
    allocationUnit: SelectableItem[];
    unit: SelectableItem[];
    alarmName: string;
};

// Provide a "schema" for runtime introspection
const variableAlarmWithTranslationsSchema: Record<keyof VariableAlarmWithTranslations, any> = {
    unitTranslated: undefined,
    usageTranslated: undefined,
    deviationNotificRuleId: undefined,
    allocationUnit: undefined,
    deviationType: undefined,
    deviationValue: undefined,
    minimumDeviationPeriod: undefined,
    activeStartTime: undefined,
    activeEndTime: undefined,
    throttle: undefined,
    meterCriteriaMismatch: undefined,
    alarmName: undefined,
    recipients: undefined,
    unit: undefined,
    meterIds: undefined,
};

@Component({
    selector: 'app-variable-alarms-table',
    imports: [
        CommonModule,
        TranslatePipe,
        LoadComponent,
        ReactiveFormsModule,
        SortableTableHeaderComponent,
        PaginationComponent,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        NgbDropdown,
        NgbDropdownItem,
        NgbDropdownMenu,
        NgbDropdownToggle,
    ],
    templateUrl: './variable-alarms-table.component.html',
    styleUrl: './variable-alarms-table.component.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariableAlarmsTableComponent implements OnDestroy, OnInit {
    protected variableAlarmsSubject: BehaviorSubject<VariableAlarmWithTranslations[]> =
        new BehaviorSubject<VariableAlarmWithTranslations[]>([]);
    private readonly headers = viewChildren(SortableTableHeaderComponent);
    @Input() buildingNo: string | null = null;
    @Input() set variableAlarms(variableAlarms: VariableAlarmWithTranslations[]) {
        this.variableAlarmsSubject.next(variableAlarms);
    }
    @Output() deleteVariableAlarm: EventEmitter<VariableAlarmWithTranslations> =
        new EventEmitter<VariableAlarmWithTranslations>();

    @Output() editAlarm: EventEmitter<string | null> = new EventEmitter<string | null>();
    @Output() duplicateAlarm: EventEmitter<VariableAlarmWithTranslations | null> =
        new EventEmitter<VariableAlarmWithTranslations | null>();
    formGroupFilter: FormGroup<{
        meterCriteriaMismatch: FormControl<SelectableItem[] | null>;
        usageTranslated: FormControl<SelectableItem[] | null>;
        unitTranslated: FormControl<SelectableItem[] | null>;
        alarmName: FormControl<string | null>;
    }> = this.formBuilder.group({
        meterCriteriaMismatch: new FormControl<SelectableItem[]>([
            {
                name: this.translateService.translate(
                    'consumption-agents_Meter-list-must-be-updated',
                ),
                selected: false,
            },
            {
                name: this.translateService.translate('common_All_connected_devices_supported'),
                selected: false,
            },
        ]),
        usageTranslated: new FormControl<SelectableItem[]>([]),
        unitTranslated: new FormControl<SelectableItem[]>([]),
        alarmName: new FormControl<string | null>(''),
    });
    columnHeads: ColumnHeader[] = [
        {
            label: this.translateService.translate('common_configuration-status'),
            column: 'meterCriteriaMismatch',
            formControl: this.formGroupFilter.controls.meterCriteriaMismatch,
            formType: 'selectMultiple',
        },
        {
            label: this.translateService.translate('monitoring_alarm-name'),
            column: 'alarmName',
            formControl: this.formGroupFilter.controls.alarmName,
            formType: 'text',
        },
        {
            label: this.translateService.translate('properties_usage', 'online', 'systems'),
            column: 'usageTranslated',
            formControl: this.formGroupFilter.controls.usageTranslated,
            formType: 'selectMultiple',
        },
        {
            label: this.translateService.translate('properties_unit', 'online', 'systems'),
            column: 'unitTranslated',
            formControl: this.formGroupFilter.controls.unitTranslated,
            formType: 'selectMultiple',
        },
    ];

    readonly tableSettings$ = combineLatest([
        this.variableAlarmsService.sorting.pipe(filterNulls()),
        this.variableAlarmsService.page,
        this.variableAlarmsService.pageSize,
    ]).pipe(
        map(([sorting, page, pageSize]) => ({
            sorting: sorting,
            page: page,
            pageSize: pageSize,
        })),
    );

    readonly tableValue$ = combineLatest([
        fromControl(this.formGroupFilter),
        this.tableSettings$,
        this.variableAlarmsSubject,
    ]).pipe(
        map(([filterControl, settings, variableAlarms]) => {
            const filteredCollection = variableAlarms.filter(variableAlarm =>
                this.variableAlarmFilter(variableAlarm, {
                    meterCriteriaMismatch: filterControl.meterCriteriaMismatch ?? [],
                    allocationUnit: filterControl.usageTranslated ?? [],
                    unit: filterControl.unitTranslated ?? [],
                    alarmName: filterControl.alarmName ?? '',
                }),
            );

            const sortedVariableAlarms = this.sortVariableAlarms(
                filteredCollection,
                settings.sorting.column,
                settings.sorting.direction,
            );
            const slicedVariableAlarms = sortedVariableAlarms.slice(
                (settings.page - 1) * settings.pageSize,
                settings.page * settings.pageSize,
            );

            return {
                settings: settings,
                page: settings.page,
                pageSize: settings.pageSize,
                variableAlarms: slicedVariableAlarms,
                collectionSize: slicedVariableAlarms.length,
                noDataFromApi: variableAlarms.length === 0,
            };
        }),
        tap(view =>
            setTimeout(() => {
                applySortingToHeaders(this.headers(), view.settings.sorting);
            }, 0),
        ),
    );
    readonly sortedTableValues$ = combineLatest([
        this.tableValue$,
        this.variableAlarmsService.sorting,
    ]).pipe(
        map(([tableValue, sorting]) => ({
            ...tableValue,
            variableAlarms: this.sortVariableAlarms(
                tableValue.variableAlarms,
                sorting.column,
                sorting.direction,
            ),
        })),
    );

    constructor(
        private formBuilder: FormBuilder,
        private variableAlarmsService: VariableAlarmsService,
        private translateService: TranslateService,
    ) {
        this.variableAlarmsService.filterSubject.pipe(take(1)).subscribe(filterData => {
            if (filterData != null) {
                this.formGroupFilter.patchValue(filterData);
            }
        });
    }
    ngOnInit(): void {
        this.formGroupFilter.patchValue({
            unitTranslated: this.getUniqueValuesForLists(
                this.variableAlarmsSubject.value,
                'unitTranslated',
            ),
            usageTranslated: this.getUniqueValuesForLists(
                this.variableAlarmsSubject.value,
                'usageTranslated',
            ),
        });
    }
    ngOnDestroy(): void {
        this.variableAlarmsService.setFilterColumns({
            alarmName: this.formGroupFilter.value.alarmName ?? '',
            alloUnitType: this.formGroupFilter.value.usageTranslated ?? [],
            unit: this.formGroupFilter.value.unitTranslated ?? [],
        });
    }

    setPage(page: number): void {
        this.variableAlarmsService.page.next(page);
    }
    setPageSize(pageSize: number): void {
        this.variableAlarmsService.pageSize.next(pageSize);
    }

    onSort(sorting: Sorting): void {
        applySortingToHeaders(this.headers(), sorting);
        this.variableAlarmsService.updateSorting(sorting);
    }

    private getUniqueValuesForLists(
        consumptionAgent: VariableAlarmWithTranslations[],
        key: 'unitTranslated' | 'usageTranslated',
    ): SelectableItem[] {
        const valueList: Array<SelectableItem> = consumptionAgent.map(agent => ({
            name:
                key === 'unitTranslated'
                    ? (agent.unitTranslated ?? this.translateService.translate('common_NO_VALUE'))
                    : agent.usageTranslated,
            selected: false,
            sortOrder: agent.unitTranslated != null ? 1 : 0,
        }));
        return uniqBy(valueList, item => item.name).sort(
            composeComparators([
                keyComparator(item => item.sortOrder),
                keyComparator(item => item.name),
            ]),
        );
    }
    private variableAlarmFilter(
        variableAlarm: VariableAlarmWithTranslations,
        filter: Filter,
    ): boolean {
        const meterMisMatch = this.filterVariableAlarmFromList(
            variableAlarm.meterCriteriaMismatch,
            filter.meterCriteriaMismatch,
            'meterCriteriaMismatch',
        );
        const unitTranslated = this.filterVariableAlarmFromList(
            variableAlarm.unitTranslated != null
                ? variableAlarm.unitTranslated
                : this.translateService.translate('common_NO_VALUE'),
            filter.unit,
            'unitTranslated',
        );
        const usageTranslated = this.filterVariableAlarmFromList(
            variableAlarm.usageTranslated,
            filter.allocationUnit,
            'usageTranslated',
        );
        const alarmNameMatch = includesIgnoreCase(variableAlarm.alarmName, filter.alarmName);

        return meterMisMatch && unitTranslated && usageTranslated && alarmNameMatch;
    }

    private filterVariableAlarmFromList(
        value: string | number | boolean | null,
        selectableItem: SelectableItem[],
        parameter?: string,
    ): boolean {
        const valuesToFilter = selectableItem
            .filter(it => it.selected)
            .map(it => it.name.toString());
        if (valuesToFilter.length === 0 || selectableItem.length === 0) {
            return true;
        }
        switch (parameter) {
            case 'meterCriteriaMismatch':
                return valuesToFilter.includes(
                    this.translateService.translate(
                        value === true
                            ? 'consumption-agents_Meter-list-must-be-updated'
                            : 'common_All_connected_devices_supported',
                    ),
                );
            default:
                return valuesToFilter.includes(value != null ? value.toString() : '');
        }
    }

    private getColumnComparator(
        column: keyof VariableAlarmWithTranslations,
        order: 'asc' | 'desc' = 'asc',
    ): (a: VariableAlarmWithTranslations, b: VariableAlarmWithTranslations) => number {
        switch (column) {
            case 'alarmName':
            case 'usageTranslated':
            case 'unitTranslated':
                return caseInsensitiveComparator(
                    variableAlarm => variableAlarm[column] ?? '',
                    order,
                );
            default:
                return keyComparator(variableAlarm => variableAlarm[column]?.toString(), order);
        }
    }

    private sortVariableAlarms(
        variableAlarms: VariableAlarmWithTranslations[],
        column: string,
        order: 'asc' | 'desc',
    ): VariableAlarmWithTranslations[] {
        // Type guard: Check if the column is a valid key of VariableAlarmWithTranslations
        if (this.isKeyOfVariableAlarmWithTranslations(column)) {
            const columnComparator = this.getColumnComparator(column, order);
            return [...variableAlarms].sort(columnComparator);
        } else {
            console.warn(`Invalid column: ${column}`);
            return variableAlarms; // Return unsorted data if column is invalid
        }
    }

    // Helper method to validate `column` as a key of VariableAlarmWithTranslations
    private isKeyOfVariableAlarmWithTranslations(
        key: string,
    ): key is keyof VariableAlarmWithTranslations {
        // Use Object.keys to check if `key` is a valid key
        return Object.keys(variableAlarmWithTranslationsSchema).includes(key);
    }
}
